import * as Sentry from '@sentry/nextjs'
import merge from 'lodash/merge'

import { GutHealthRetestTerms } from '@/components/elements/products/GutHealthRetestTerms'
import { MembershipIncGHRetestTerms } from '@/components/elements/products/MembershipIncGHRetestTerms'
import { MembershipStarterFreeTrialTerms } from '@/components/elements/products/MembershipStarterFreeTrialTerms'
import { MembershipTerms } from '@/components/elements/products/MembershipTerms'
import { MembershipTierDefaultTerms } from '@/components/elements/products/MembershipTierDefaultTerms'

import { GB_DEFAULT_PREORDER_PLANS_PAGE, PreorderPlansPath } from '@/lib/checkoutConstants'

import {
  AutoGenerateProductDetail,
  CHECKOUT_GB,
  CHECKOUT_US,
  CheckoutCountry,
  CustomProductDetail,
  KitOption,
  Prices,
  Product,
  ProductName,
  Products,
} from './types'

export const getProduct = (productName: ProductName, products: Products): Product => {
  const productWithDefaultInfo = products[productName]
  const customProductInfo = customProducts[productName]
  if (!customProductInfo) {
    const error = new Error(`Product ${productName} is not defined in customProducts`)
    Sentry.captureException(error)
    console.error(error)
  }
  return merge(productWithDefaultInfo, customProductInfo)
}

export const getDefaultProductInfo = (formattedStripePrices: Prices) => {
  const autoGeneratedProductDetails = generateProductInfoForAllPrices(formattedStripePrices)
  return merge(formattedStripePrices, autoGeneratedProductDetails)
}

// *****
// Default country checkout products configuration
// *****

type ProductConfig = {
  baseMembershipPrice: number
  baseKitPrice: number
  fullPriceKit: KitOption
  instalmentsKit: KitOption
}

type ProductConfigLookup = {
  [key in CheckoutCountry]: ProductConfig
}

type PreorderConfigLookup = {
  [key in PreorderPlansPath]: ProductConfig
}

// *****
// Autogenerate product info for each Stripe price
// *****

type ProductType = 'Membership' | 'Kit'

const displayName = (productType: ProductType, nickname: string) => {
  return productType === 'Membership' ? nickname : 'ZOE Test Kit & Report'
}

const productImage = (productType: ProductType) => {
  return productType === 'Membership'
    ? '/assets/img/checkout/products/app.png'
    : '/assets/img/checkout/products/kit.png'
}

const itemType = (lookupKey: string, productType: ProductType) => {
  if (lookupKey.toLowerCase().includes('instalments')) {
    return 'recurring'
  }

  return productType === 'Membership' ? 'recurring' : 'one_time'
}

const generateProductInfoForAllPrices = (prices: Prices): { [x: string]: AutoGenerateProductDetail } => {
  return Object.keys(prices).reduce((products, lookupKey) => {
    const nickname = prices[lookupKey].nickname
    const nicknameLowercased = nickname.toLowerCase()
    const productType: ProductType = nicknameLowercased.includes('membership') ? 'Membership' : 'Kit'

    return {
      ...products,
      ...{
        [lookupKey]: {
          ...{
            displayName: displayName(productType, nickname),
            productImage: productImage(productType),
            productCategory: productType,
            itemType: itemType(lookupKey, productType),
          },
        },
      },
    }
  }, {})
}

// *****
// Custom product info where we want to override auto-generated product info
// *****

// Display Products

const gutHeathRetest: Product = {
  displayName: 'Gut Health Retest',
  productCategory: 'DisplayProduct',
  itemType: 'display_product',
  productImage: '/assets/img/checkout/products/kit-2-old-yellow-bg.png',
  displayPrice: 'FREE',
  currency: 'gbp',
  price: 0,
  zoeProductId: 'GUT_HEALTH_RETEST_DISPLAY_PRODUCT',
  payNow: false,
  country: CHECKOUT_GB,
  nickname: 'Gut health retest',
  stripePriceId: '',
  productInfo: 'Included after 4 months, worth £199',
}

// US Products
const usMonthlyProgram: CustomProductDetail = {
  productInfo: 'Billed when membership starts.',
  terms: MembershipTerms,
}

const usFourMonthlyWithTrialProgram: CustomProductDetail = {
  displayName: '14 day trial membership',
  productCategory: 'TrialMembership',
  productInfo: 'Cancel anytime',
  displayPrice: '$39.00',
  displayPriceAfterDiscount: 'FREE',
  terms: undefined,
}

const usFourMonthlyProgram: CustomProductDetail = {
  productInfo: 'Starts when you get test results.\n$39/mo paid in one installment.',
  terms: MembershipTerms,
}

const usYearlyProgram: CustomProductDetail = {
  productInfo: 'Billed when membership starts.',
  terms: MembershipTerms,
}

// US WW Products
const usPricingExpAug24Starter: CustomProductDetail = {
  displayName: '4-Month Membership',
  additionalPriceInfo: '%%price%% total. Paid monthly. Starts on %%date%%. Auto-renews every 4 months until cancelled.',
  terms: MembershipTierDefaultTerms,
  membershipCommitmentMonths: 4,
}

const usPricingExpSep24Starter: CustomProductDetail = {
  displayName: '4-Month Membership',
  additionalPriceInfo: '%%price%% total. Paid monthly. Starts on %%date%%. Auto-renews every 4 months until cancelled.',
  terms: MembershipTierDefaultTerms,
  membershipCommitmentMonths: 4,
}

const usPricingExpAug24StarterTestKit: CustomProductDetail = {
  displayName: 'ZOE Testing',
  payNow: true,
  additionalPriceInfo: 'Blood sugar, blood fat, and gut health.',
  additionalDetails: 'You must complete your first day of testing within 2 weeks of receiving your kit.',
}

const usPricingExpSep24StarterTestKit: CustomProductDetail = {
  displayName: 'ZOE Testing',
  payNow: true,
  additionalPriceInfo: 'Blood sugar, blood fat, and gut health.',
  additionalDetails: 'You must complete your first day of testing within 2 weeks of receiving your kit.',
}

// Thrive
const usPricingExpAug24Thrive: CustomProductDetail = {
  displayName: '12-Month Membership',
  additionalPriceInfo:
    '%%price%% total. Paid monthly. Starts on %%date%%. Auto-renews every 12 months until cancelled.',
  terms: MembershipTierDefaultTerms,
  membershipCommitmentMonths: 12,
}

const usPricingExpSep24Thrive: CustomProductDetail = {
  displayName: '12-Month Membership',
  additionalPriceInfo:
    '%%price%% total. Paid monthly. Starts on %%date%%. Auto-renews every 12 months until cancelled.',
  terms: MembershipTierDefaultTerms,
  membershipCommitmentMonths: 12,
}

const usPricingExpAug24ThriveTestKit: CustomProductDetail = {
  displayName: 'ZOE Testing',
  additionalPriceInfo: 'Blood sugar, blood fat, gut health, and one gut health retest.',
}

const usPricingExpSep24ThriveTestKit: CustomProductDetail = {
  displayName: 'ZOE Testing',
  additionalPriceInfo: 'Blood sugar, blood fat, gut health, and one gut health retest.',
}

// Thrive Plus
const usPricingExpAug24ThrivePlus: CustomProductDetail = {
  displayName: '12-Month Membership',
  additionalPriceInfo:
    '%%price%% total. Paid monthly. Starts on %%date%%. Auto-renews every 12 months until cancelled.',
  terms: MembershipTierDefaultTerms,
  membershipCommitmentMonths: 12,
}

const usPricingExpSep24ThrivePlus: CustomProductDetail = {
  displayName: '12-Month Membership',
  additionalPriceInfo:
    '%%price%% total. Paid monthly. Starts on %%date%%. Auto-renews every 12 months until cancelled.',
  terms: MembershipTierDefaultTerms,
  membershipCommitmentMonths: 12,
}

const usPricingExpAug24ThrivePlusTestKit: CustomProductDetail = {
  displayName: 'ZOE Testing',
  additionalPriceInfo: 'Blood sugar, blood fat, gut health, and three gut health retests',
}

const usPricingExpSep24ThrivePlusTestKit: CustomProductDetail = {
  displayName: 'ZOE Testing',
  additionalPriceInfo: 'Blood sugar, blood fat, gut health, and three gut health retests',
}

// US new pricing
const usStartDec24: CustomProductDetail = {
  displayName: '4-Month Membership',
  additionalPriceInfo: '%%price%% total. Paid monthly. Starts on %%date%%. Auto-renews every 4 months until cancelled.',
  terms: MembershipTierDefaultTerms,
  membershipCommitmentMonths: 4,
}

const usStartDec24TestKit: CustomProductDetail = {
  displayName: 'ZOE Testing',
  additionalPriceInfo: 'Blood sugar, blood fat, and gut health.',
  additionalDetails: 'You must complete your first day of testing within 2 weeks of receiving your kit.',
}

const usCoreDec24: CustomProductDetail = {
  displayName: '12-Month Membership',
  additionalPriceInfo:
    '%%price%% total. Paid monthly. Starts on %%date%%. Auto-renews every 12 months until cancelled.',
  terms: MembershipTierDefaultTerms,
  membershipCommitmentMonths: 12,
}

const usCoreDec24TestKit: CustomProductDetail = {
  displayName: 'ZOE Testing',
  additionalPriceInfo: 'Blood sugar, blood fat, and gut health.',
  additionalDetails: 'You must complete your first day of testing within 2 weeks of receiving your kit.',
}

const usCompleteDec24: CustomProductDetail = {
  displayName: '12-Month Membership',
  additionalPriceInfo:
    '%%price%% total. Paid monthly. Starts on %%date%%. Auto-renews every 12 months until cancelled.',
  terms: MembershipTierDefaultTerms,
  membershipCommitmentMonths: 12,
}

const usCompleteDec24TestKit: CustomProductDetail = {
  displayName: 'ZOE Testing',
  additionalPriceInfo: 'Blood sugar, blood fat, gut health, and one gut health retest.',
}

// UK Products -- GB prefix
const gbMonthlyProgram: CustomProductDetail = {
  productInfo: 'Begins when you get your test results.',
  displayNameOverride: '1 Month Membership',
  terms: MembershipTerms,
}

const gbFourMonthlyProgram: CustomProductDetail = {
  productInfo: 'Begins when you get your test results. Paid in one payment.',
  monthlyPrice: '£39.99/mo',
  additionalPriceInfo: '£39.99/mo billed every 4 months',
  terms: MembershipTerms,
}

const gbYearlyProgram: CustomProductDetail = {
  productInfo: 'Begins when you get your test results. Paid in one payment.',
  monthlyPrice: '£24.99/mo',
  additionalPriceInfo: '£24.99/mo billed yearly',
  terms: MembershipTerms,
}

const gb6InstallmentKit: CustomProductDetail = {
  installmentTotalPrice: 29994,
}

const gbGutHealthRetest: CustomProductDetail = {
  displayName: 'Gut health retest',
  payNow: true,
  terms: GutHealthRetestTerms,
  productCategory: 'GutHealthRetest',
  additionalPriceInfo: 'Measure your progress in improving your gut health.',
  productImage: '/assets/img/checkout/products/kit-2.png',
}

// Starter
// Oct23
const gbPricingExpOct23BranchBStarter: CustomProductDetail = {
  displayName: '4-Month Membership',
  additionalPriceInfo: '%%price%% total. Starts on %%date%%.',
  terms: MembershipStarterFreeTrialTerms,
  membershipCommitmentMonths: 4,
}

const gbPricingExpOct23BranchBStarterTestKit: CustomProductDetail = {
  displayName: 'ZOE Testing',
  payNow: false,
  additionalPriceInfo: 'Blood sugar, blood fat, gut health',
}

// Jan24
const gbPricingExpJan24BranchBStarter: CustomProductDetail = {
  displayName: '4-Month Membership',
  additionalPriceInfo: '%%price%% total. Paid monthly. Starts on %%date%%.',
  terms: MembershipTierDefaultTerms,
  membershipCommitmentMonths: 4,
}

const gbPricingExpJan24BranchBStarterTestKit: CustomProductDetail = {
  displayName: 'ZOE Testing',
  payNow: true,
  additionalPriceInfo: 'Blood sugar, blood fat, and gut health.',
  additionalDetails: 'You must complete your first day of testing within 2 weeks of receiving your kit.',
}

// Thrive
const gbPricingExpOct23BranchBThrive: CustomProductDetail = {
  displayName: '12-Month Membership',
  additionalPriceInfo: '%%price%% total. Paid monthly. Starts on %%date%%.',
  terms: MembershipTierDefaultTerms,
  membershipCommitmentMonths: 12,
}

const gbPricingExpOct23BranchBThriveTestKit: CustomProductDetail = {
  displayName: 'ZOE Testing',
  additionalPriceInfo: 'Blood sugar, blood fat, gut health, and one gut health retest.',
}

// Thrive Plus
const gbPricingExpOct23BranchBThrivePlus: CustomProductDetail = {
  displayName: '12-Month Membership',
  additionalPriceInfo: '%%price%% total. Paid monthly. Starts on %%date%%.',
  terms: MembershipTierDefaultTerms,
  membershipCommitmentMonths: 12,
}

const gbPricingExpOct23BranchBThrivePlusTestKit: CustomProductDetail = {
  displayName: 'ZOE Testing',
  additionalPriceInfo: 'Blood sugar, blood fat, gut health, and three gut health retests',
}
// End pricing exp Oct 23

// GB new pricing
const gbStartDec24: CustomProductDetail = {
  displayName: '4-Month Membership',
  additionalPriceInfo: '%%price%% total. Paid monthly. Starts on %%date%%. Auto-renews every 4 months until cancelled.',
  terms: MembershipTierDefaultTerms,
  membershipCommitmentMonths: 4,
}

const gbStartDec24TestKit: CustomProductDetail = {
  displayName: 'ZOE Testing',
  additionalPriceInfo: 'Blood sugar, blood fat, and gut health.',
  additionalDetails: 'You must complete your first day of testing within 2 weeks of receiving your kit.',
}

const gbCoreDec24: CustomProductDetail = {
  displayName: '12-Month Membership',
  additionalPriceInfo:
    '%%price%% total. Paid monthly. Starts on %%date%%. Auto-renews every 12 months until cancelled.',
  terms: MembershipTierDefaultTerms,
  membershipCommitmentMonths: 12,
}

const gbCoreDec24TestKit: CustomProductDetail = {
  displayName: 'ZOE Testing',
  additionalPriceInfo: 'Blood sugar, blood fat, and gut health.',
  additionalDetails: 'You must complete your first day of testing within 2 weeks of receiving your kit.',
}

const gbCompleteDec24: CustomProductDetail = {
  displayName: '12-Month Membership',
  additionalPriceInfo:
    '%%price%% total. Paid monthly. Starts on %%date%%. Auto-renews every 12 months until cancelled.',
  terms: MembershipTierDefaultTerms,
  membershipCommitmentMonths: 12,
}

const gbCompleteDec24TestKit: CustomProductDetail = {
  displayName: 'ZOE Testing',
  additionalPriceInfo: 'Blood sugar, blood fat, gut health, and one gut health retest.',
}

// Daily30+ start
const gbDaily30Plus1Month: CustomProductDetail = {
  displayName: 'ZOE Daily30+',
  additionalPriceInfo: 'Monthly subscription. Sent each month until cancelled.',
  payNow: true,
}

const gbDaily30Plus4Month: CustomProductDetail = {
  displayName: 'ZOE Daily30+',
  additionalPriceInfo: '4-month subscription. Sent every 4 months until cancelled.',
  payNow: true,
}

const usDaily30Plus1Month: CustomProductDetail = {
  displayName: 'ZOE Daily30+',
  additionalSubscriptionDurationInfo: 'Monthly subscription.',
  additionalPriceInfo: 'Auto renews every month until cancelled.',
  payNow: true,
}

const usDaily30Plus4Month: CustomProductDetail = {
  displayName: 'ZOE Daily30+',
  additionalSubscriptionDurationInfo: 'Four-month subscription.',
  additionalPriceInfo: 'Auto renews every four months until cancelled.',
  payNow: true,
}

// Daily30+ end

const customProducts: { [key in ProductName]?: CustomProductDetail } = {
  // US Products
  PROGRAM_MONTHLY: usMonthlyProgram,
  PROGRAM_4_MONTH_TRIAL: usFourMonthlyWithTrialProgram,
  PROGRAM_4_MONTH: usFourMonthlyProgram,
  PROGRAM_YEARLY: usYearlyProgram,

  // US WW Products
  US_TEST_KIT_AUG24_TIER1_1_INSTALMENT: usPricingExpAug24StarterTestKit,
  US_TEST_KIT_SEP24_TIER1_1_INSTALMENT: usPricingExpSep24StarterTestKit,
  US_PROGRAM_4_MONTH_AUG24_TIER1_MONTHLY_ROLLING: usPricingExpAug24Starter,
  US_PROGRAM_4_MONTH_SEP24_TIER1_MONTHLY_ROLLING: usPricingExpSep24Starter,

  US_TEST_KIT_AUG24_TIER2_1_INSTALMENT: usPricingExpAug24ThriveTestKit,
  US_TEST_KIT_SEP24_TIER2_1_INSTALMENT: usPricingExpSep24ThriveTestKit,
  US_PROGRAM_12_MONTH_AUG24_TIER2_MONTHLY_ROLLING: usPricingExpAug24Thrive,
  US_PROGRAM_12_MONTH_SEP24_TIER2_MONTHLY_ROLLING: usPricingExpSep24Thrive,

  US_TEST_KIT_AUG24_TIER3_1_INSTALMENT: usPricingExpAug24ThrivePlusTestKit,
  US_TEST_KIT_SEP24_TIER3_1_INSTALMENT: usPricingExpSep24ThrivePlusTestKit,
  US_PROGRAM_12_MONTH_AUG24_TIER3_MONTHLY_ROLLING: usPricingExpAug24ThrivePlus,
  US_PROGRAM_12_MONTH_SEP24_TIER3_MONTHLY_ROLLING: usPricingExpSep24ThrivePlus,

  // US New Pricing
  US_TEST_KIT_START_DEC24: usStartDec24TestKit,
  US_PROGRAM_4_MONTH_START_DEC24_MONTHLY_ROLLING: usStartDec24,
  US_TEST_KIT_CORE_DEC24: usCoreDec24TestKit,
  US_PROGRAM_12_MONTH_CORE_DEC24_MONTHLY_ROLLING: usCoreDec24,
  US_TEST_KIT_COMPLETE_DEC24: usCompleteDec24TestKit,
  US_PROGRAM_12_MONTH_COMPLETE_DEC24_MONTHLY_ROLLING: usCompleteDec24,

  // GB Products
  GB_PROGRAM_MONTHLY: gbMonthlyProgram,
  GB_PROGRAM_4_MONTH: gbFourMonthlyProgram,
  GB_PROGRAM_YEARLY: gbYearlyProgram,

  GB_TEST_KIT_PREORDER_6_INSTALMENTS_A: gb6InstallmentKit,

  GB_PROGRAM_4_MONTH_DEC23_TIER1_MONTHLY_ROLLING: gbPricingExpOct23BranchBStarter,
  GB_FREE_TRIAL_TEST_KIT_DEC23_TIER1_1_INSTALMENT: gbPricingExpOct23BranchBStarterTestKit,

  GB_PROGRAM_12_MONTH_DEC23_TIER2_MONTHLY_ROLLING: gbPricingExpOct23BranchBThrive,
  GB_TEST_KIT_DEC23_TIER2_1_INSTALMENT: gbPricingExpOct23BranchBThriveTestKit,

  GB_PROGRAM_12_MONTH_OCT23_TIER3_MONTHLY_ROLLING: gbPricingExpOct23BranchBThrivePlus,
  GB_TEST_KIT_OCT23_TIER3_1_INSTALMENT: gbPricingExpOct23BranchBThrivePlusTestKit,

  GB_TEST_KIT_JAN24_TIER1_1_INSTALMENT: gbPricingExpJan24BranchBStarterTestKit,
  GB_PROGRAM_4_MONTH_JAN24_TIER1_MONTHLY_ROLLING: gbPricingExpJan24BranchBStarter,

  // GB New Pricing
  GB_TEST_KIT_START_DEC24: gbStartDec24TestKit,
  GB_PROGRAM_4_MONTH_START_DEC24_MONTHLY_ROLLING: gbStartDec24,
  GB_TEST_KIT_CORE_DEC24: gbCoreDec24TestKit,
  GB_PROGRAM_12_MONTH_CORE_DEC24_MONTHLY_ROLLING: gbCoreDec24,
  GB_TEST_KIT_COMPLETE_DEC24: gbCompleteDec24TestKit,
  GB_PROGRAM_12_MONTH_COMPLETE_DEC24_MONTHLY_ROLLING: gbCompleteDec24,

  // Gut heath Retest
  GB_GUT_HEALTH_RETEST: gbGutHealthRetest,

  GB_DAILY30_1_MONTH: gbDaily30Plus1Month,
  GB_DAILY30_4_MONTH: gbDaily30Plus4Month,
  US_DAILY30_1_MONTH: usDaily30Plus1Month,
  US_DAILY30_4_MONTH: usDaily30Plus4Month,
} as const
